import type {
  Catalog,
  CatalogEntry,
  MaterialCategory,
  MaterialImportRow,
} from '../model/schema.js';
import { findCatalogEntry, matchCatalogToken, normalizeToken } from '../catalog/catalog.js';
import type { ModelledPosition, ReconcileRow, ReconcileStatus } from './types.js';

/** Filler words of material names that carry no identity ("Shure SM57 Mikrofon"). */
export const NOISE: ReadonlySet<string> = new Set([
  'mikrofon',
  'mikrophon',
  'mikro',
  'microphone',
  'mic',
  'monitorlautsprecher',
  'lautsprecher',
  'digital',
  'system',
  'stereo',
  'set',
  'stk',
  'stück',
  'pcs',
]);

/** Words of a material name: lower case, `d&b` → `dnb`, filler words removed. */
export function materialWords(name: string): string[] {
  return name
    .toLowerCase()
    .replace(/d\s*&\s*b/gu, 'dnb')
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w !== '' && !NOISE.has(w));
}

/** Normalised material name for comparisons ("Shure SM57 Mikrofon" → "shuresm57"). */
export function normalizeMaterialName(name: string): string {
  return normalizeToken(materialWords(name).join(''));
}

/**
 * Catalog entry a free material name refers to: the whole name, else the longest run of words
 * that is an exact catalog token ("Sennheiser e 604" → e 604; "Neumann KM 184" → KM 184).
 */
export function matchMaterialName(
  entries: readonly CatalogEntry[],
  name: string,
): CatalogEntry | undefined {
  const words = materialWords(name);
  for (let size = words.length; size >= 1; size--) {
    for (let start = 0; start + size <= words.length; start++) {
      const token = words.slice(start, start + size).join(' ');
      const hit = matchCatalogToken(entries, token);
      if (hit !== undefined) return hit;
    }
  }
  return undefined;
}

export interface ReconcileOptions {
  /** Imported name → catalog ref or modelled name (`meta.materialList.aliases`). */
  aliases?: Readonly<Record<string, string>>;
  catalogs?: readonly Catalog[];
  /** Keys of rows the user ignores (flagged, not removed). */
  ignored?: readonly string[];
}

type Via = NonNullable<ReconcileRow['via']>;

interface Side {
  key: string;
  via: Via;
  catalogRef?: string;
}

function keyOfName(entries: readonly CatalogEntry[], name: string, via: Via): Side {
  const entry = matchMaterialName(entries, name);
  if (entry !== undefined)
    return {
      key: `ref:${entry.ref}`,
      via: via === 'exact' ? 'catalog' : via,
      catalogRef: entry.ref,
    };
  return { key: `name:${normalizeMaterialName(name)}`, via };
}

function aliasTarget(aliases: Readonly<Record<string, string>>, name: string): string | undefined {
  if (Object.hasOwn(aliases, name)) return aliases[name];
  const n = normalizeMaterialName(name);
  return Object.entries(aliases).find(([k]) => normalizeMaterialName(k) === n)?.[1];
}

interface Group {
  key: string;
  fuzzy?: boolean;
  catalogRef?: string;
  category?: MaterialCategory;
  modelled: ModelledPosition[];
  imported: { index: number; row: MaterialImportRow; via: Via }[];
}

/** Display name of a reconciliation row (every row has at least one side). */
const nameOf = (r: ReconcileRow): string => r.modelledName ?? r.importedName!;

const STATUS_ORDER: Record<ReconcileStatus, number> = {
  'quantity-diff': 0,
  'modelled-only': 1,
  'imported-only': 2,
  matched: 3,
};

function unique(names: readonly string[]): string | undefined {
  const u = [...new Set(names)];
  return u.length === 0 ? undefined : u.join(' / ');
}

/** Overlap score of two normalised names (0 = unrelated): length of the contained one. */
function overlap(a: string, b: string): number {
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return short.length >= 4 && long.includes(short) ? short.length : 0;
}

/**
 * Reconciles the modelled material list with an imported one (decision U11). Names are matched
 * by user alias, catalog entry (whole name or a run of words, filler words ignored), normalised
 * name and finally fuzzily (one normalised name contains the other). Result rows: in both
 * (`matched`, also when the list includes the spares), `modelled-only`, `imported-only`,
 * `quantity-diff`; sorted by status (differences first), then name.
 */
export function reconcileMaterials(
  modelled: readonly ModelledPosition[],
  imported: readonly MaterialImportRow[],
  opts: ReconcileOptions = {},
): ReconcileRow[] {
  const catalogs = opts.catalogs ?? [];
  const entries = catalogs.flatMap((c) => c.entries);
  const aliases = opts.aliases ?? {};
  const groups = new Map<string, Group>();
  const group = (side: Side): Group => {
    let g = groups.get(side.key);
    if (g === undefined) {
      g = { key: side.key, modelled: [], imported: [] };
      groups.set(side.key, g);
    }
    if (side.catalogRef !== undefined) g.catalogRef = side.catalogRef;
    return g;
  };
  for (const m of modelled) {
    const side: Side =
      m.catalogRef !== undefined
        ? { key: `ref:${m.catalogRef}`, via: 'exact', catalogRef: m.catalogRef }
        : keyOfName(entries, m.name, 'exact');
    const g = group(side);
    g.modelled.push(m);
    g.category ??= m.category;
  }
  imported.forEach((row, index) => {
    const target = aliasTarget(aliases, row.name);
    let side: Side;
    if (target === undefined) side = keyOfName(entries, row.name, 'exact');
    else if (findCatalogEntry(catalogs, target) !== undefined) {
      side = { key: `ref:${target}`, via: 'alias', catalogRef: target };
    } else side = keyOfName(entries, target, 'alias');
    const g = group(side);
    g.imported.push({ index, row, via: side.via });
    if (row.category !== undefined) g.category ??= row.category;
  });

  // Fuzzy pass: imported-only groups join the modelled-only group whose name overlaps most.
  const all = [...groups.values()];
  for (const g of all.filter((x) => x.modelled.length === 0)) {
    const a = normalizeMaterialName(g.imported[0]!.row.name);
    let best: { target: Group; score: number } | undefined;
    for (const target of all) {
      if (target.imported.length > 0) continue;
      const score = Math.max(
        ...target.modelled.map((m) => overlap(a, normalizeMaterialName(m.name))),
      );
      if (score > 0 && (best === undefined || score > best.score)) best = { target, score };
    }
    if (best === undefined) continue;
    best.target.imported.push(...g.imported);
    best.target.fuzzy = true;
    best.target.category ??= g.category;
    groups.delete(g.key);
  }

  const ignored = new Set(opts.ignored ?? []);
  return [...groups.values()]
    .map((g): ReconcileRow => {
      const modelledQty = g.modelled.reduce((s, m) => s + m.count, 0);
      const spares = g.modelled.reduce((s, m) => s + (m.spares ?? 0), 0);
      const importedQty = g.imported.reduce((s, i) => s + i.row.qty, 0);
      const status: ReconcileStatus =
        g.modelled.length === 0
          ? 'imported-only'
          : g.imported.length === 0
            ? 'modelled-only'
            : importedQty === modelledQty || importedQty === modelledQty + spares
              ? 'matched'
              : 'quantity-diff';
      const row: ReconcileRow = {
        key: g.key,
        status,
        modelledQty,
        importedQty,
        diff: importedQty - modelledQty,
        modelledKeys: g.modelled.map((m) => m.rowKey),
        importedIndices: g.imported.map((i) => i.index),
        ignored: ignored.has(g.key),
      };
      const modelledName = unique(g.modelled.map((m) => m.name));
      const importedName = unique(g.imported.map((i) => i.row.name));
      if (modelledName !== undefined) row.modelledName = modelledName;
      if (importedName !== undefined) row.importedName = importedName;
      if (g.category !== undefined) row.category = g.category;
      if (g.catalogRef !== undefined) row.catalogRef = g.catalogRef;
      if (g.modelled.length > 0 && g.imported.length > 0) {
        row.via = g.fuzzy === true ? 'fuzzy' : g.imported[0]!.via;
      }
      return row;
    })
    .sort(
      (a, b) =>
        STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
        nameOf(a).localeCompare(nameOf(b)) ||
        a.key.localeCompare(b.key),
    );
}
